// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import { isEmpty, isNil } from "lodash";
import moment from "moment";
import useSession from "Hooks/session.hook";

// STYLE
import "./../customer-management.scss";
import "./customer-location-list.scss";

// ANT COMPONENTS
import {
  ActionMenu,
  AlertBox,
  BasicModal,
  Button,
  DataTable,
} from "@life-line-lab/components-react";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Col, Input, Row, Spin } from "antd";
import DataTableFilter from "Components/shared/data-table-filter";

// TYPES
import { Sorting } from "../../../types/common.types";
import { customerLocationAtom, CustomerLocationAtomType } from "Recoil/customer_location.atom";
import { CustomerType, OrgType } from "../../../types/customer-form.types";
import {GlobalFilterAtom} from "Recoil/global_filter.atom";

interface Filters {
  [key: string]: string[] | string | boolean | null;
}

export interface FilterSelectionProps {
  type?: string;
  key: string;
  label?: string;
  name: string;
  value: string | boolean;
  options: Array<{ label: string; value: string }>;
  placeholder?: string;
  isReadonly?: boolean;
  onClear?: () => void;
}

interface CustomerLocationListComponentProps {
  forceRender?: boolean;
  extraFilters?: Filters;
  onPaginationChange?: (page?: string) => void;
  onFilter: (filters: { [key: string]: string | boolean }) => void;
  onSearch?: (text: string) => void;
  onViewDetails: (record) => void;
  onEdit: (record) => void;
  onSort: (sort: string) => void;
  onDeleteLocation: (orgID: string, tempDeletedReason: string) => void;
  onFetchCustomerLocationList: () => void;
  sorting: Sorting;
  listType: "Deleted" | "Normal";
  locationList: any[];
  isReadonly: boolean;
}

const CustomerLocationListComponent: React.FC<CustomerLocationListComponentProps> = ({
  forceRender,
  extraFilters,
  onPaginationChange,
  onFilter,
  onSearch,
  onViewDetails,
  onEdit,
  onSort,
  onDeleteLocation,
  onFetchCustomerLocationList,
  sorting,
  listType,
  locationList,
  isReadonly,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { roles, roleTypes } = useSession();

  const initialModal = {
    open: false,
    type: "approve",
    record: {},
  };

  const initialError = {};
  const initialInput = { reason: "" };

  const [modal, setModal] = useState<any>(initialModal);
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [globalFilterState, ] = useRecoilState(GlobalFilterAtom);
  const [filter, setFilter] = useState<{ [key: string]: any }>({
    ancestorOrgIDs : globalFilterState?.selectedOrgIDs?.length ? globalFilterState?.selectedOrgIDs : null
  });
  const [customerLocationStatus] = useRecoilState<CustomerLocationAtomType>(customerLocationAtom);

  useEffect(() => {
    onFilter(filter);
  }, [filter]);

  useEffect(()=>{
    if(!isEmpty(globalFilterState) && globalFilterState?.selectedOrgIDs?.length){
      setFilter({...filter, ancestorOrgIDs : globalFilterState?.selectedOrgIDs})
    }
  },[globalFilterState])

  useEffect(() => {
    if (customerLocationStatus.delete === "success") {
      resetModalState();
      onFetchCustomerLocationList();
    }
    if (customerLocationStatus.restore === "success") {
      resetModalState();
      onFetchCustomerLocationList();
    }
  }, [customerLocationStatus]);

  const generateSortableTitle = ({ title, sortBy }: { title: string; sortBy: string }) => {
    return (
      <div className="sortable-title" onClick={() => onSort(sortBy)}>
        <span className="mr-3">{title}</span>

        {sorting.sortBy === sortBy && sorting.mode === "ASC" && (
          <span className="icon">
            <CaretUpOutlined />
          </span>
        )}

        {sorting.sortBy === sortBy && sorting.mode === "DESC" && (
          <span className="icon">
            <CaretDownOutlined />
          </span>
        )}

        {sorting.sortBy !== sortBy && (
          <span className="icon">
            <CaretUpOutlined />
            <CaretDownOutlined />
          </span>
        )}
      </div>
    );
  };

  const columns: any = [
    {
      title: generateSortableTitle({
        title: t("label.customer").toUpperCase(),
        sortBy: "orgName",
      }),
      dataIndex: "orgName",
      width: "200px",
      render: (_: any, record: any) => {
        return <span className="link-style-view">{record.orgName || <span>&#11835;</span>}</span>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => onViewDetails(record),
        };
      },
    },
    {
      title: t("label.customerType").toUpperCase(),
      dataIndex: ["customerType"],
      render: (_: any, record: any) => {
        return <React.Fragment>{record.customerType || <span>&#11835;</span>}</React.Fragment>;
      },
    },
    {
      title: generateSortableTitle({
        title: t("label.company").toUpperCase(),
        sortBy: "orgName",
      }),
      dataIndex: ["orgName"],
      render: (_: any, record: any) => {
        return isNil(record.orgName) ? <span>&#11835;</span> : <span>{record.orgName}</span>;
      },
    },
    {
      title: t("label.customerLocationName").toUpperCase(),
      dataIndex: ["locationName"],
      render: (_: any, record: any) => {
        return <span>{record.locationName || t("txt.primaryAddress")}</span>;
      },
    },
    {
      title: t("label.customerLocation").toUpperCase(),
      dataIndex: ["address"],
      render: (_: any, record: any) => {
        return (
          <div>
            <div>{record?.address?.street1}</div>
            <div>{record?.address?.street2}</div>
            <div>
              {record?.address?.postcode} {record?.address?.city}
            </div>
          </div>
        );
      },
    },
    {
      title: t("label.state").toUpperCase(),
      dataIndex: "address",
      render: (_: any, record: any) => {
        return <React.Fragment>{record?.address?.state || <span>&#11835;</span>}</React.Fragment>;
      },
    },
    {
      title: t("label.contactNo").toUpperCase(),
      dataIndex: ["contactNumber"],
      render: (_: any, record: any) => {
        const contact = record.contactNumber || {};
        return isEmpty(contact) ? (
          <span>&#11835;</span>
        ) : (
          <span>{`+${contact?.countryCode} ${contact?.number}`}</span>
        );
      },
    },
  ];

  if (!isReadonly) {
    columns.push({
      title: "",
      dataIndex: "actions",
      render: (_, record) => {
        return record.primary === true ? (
          <ActionMenu
            label={<MoreOutlined />}
            actions={[
              {
                label: t("action.edit"),
                icon: <EditOutlined />,
                onClick: () => handleActionClick(record, "edit"),
              },
            ]}
          />
        ) : (
          <ActionMenu
            label={<MoreOutlined />}
            actions={[
              {
                label: t("action.edit"),
                icon: <EditOutlined />,
                onClick: () => handleActionClick(record, "edit"),
              },
              {
                label: t("action.delete"),
                icon: <DeleteOutlined />,
                onClick: () => handleActionClick(record, "delete"),
              },
            ]}
          />
        );
      },
    });
  }

  // const filteredColumns = columns.splice(1, 1)
  const filteredColumns = columns.filter((col) => {
    return col.dataIndex != "orgName" && col.dataIndex != "customerType";
  });

  const deletedColumns = [
    {
      title: t("label.locationID").toUpperCase(),
      dataIndex: "locationCode",
      render: (_: any, record: any) => {
        return <span className="link-style-view">{record.locationCode}</span>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => onViewDetails(record),
        };
      },
    },
    {
      title: t("label.customerType").toUpperCase(),
      dataIndex: ["customerType"],
      render: (_: any, record: any) => {
        return <React.Fragment>{record.customerType}</React.Fragment>;
      },
    },
    {
      title: generateSortableTitle({
        title: t("label.company").toUpperCase(),
        sortBy: "orgName",
      }),
      dataIndex: ["orgName"],
      render: (_: any, record: any) => {
        return isNil(record.orgName) ? <span>&#11835;</span> : <span>{record.orgName}</span>;
      },
    },
    {
      title: t("label.state").toLocaleUpperCase(),
      dataIndex: ["address"],
      render: (_: any, record: any) => {
        return (
          <React.Fragment>
            {record?.address?.state || <span>&#11835;</span>}
            {/* {record.address.state} */}
          </React.Fragment>
        );
      },
    },
    {
      title: t("label.contactNo").toUpperCase(),
      dataIndex: ["contactNumber"],
      render: (_: any, record: any) => {
        const contact = record.contactNumber || {};
        return isEmpty(contact) ? (
          <span>&#11835;</span>
        ) : (
          <span>{`+${contact?.countryCode} ${contact?.number}`}</span>
        );
      },
    },
    {
      // title: t('label.deletedAt').toUpperCase(),
      title: generateSortableTitle({
        title: t("label.deletedAt").toUpperCase(),
        sortBy: "tempDeletedOn",
      }),
      dataIndex: ["tempDeleteInfo"],
      render: (_: any, record: any) => {
        return (
          <span className="datetime">
            {moment(record.tempDeleteInfo.tempDeletedOn).format("DD-MM-YYYY HH:mm:ss")}
          </span>
        );
      },
    },
  ];

  const filteredDeletedColumns = deletedColumns.filter((col) => {
    return col.dataIndex != "orgName";
  });

  const handleActionClick = (record, type) => {
    switch (type) {
      case "edit": {
        onEdit(record);
        break;
      }
      case "delete": {
        setModal({ open: true, type: "delete", record });
        break;
      }
    }
  };

  const handleSearch = async (text: string) => {
    setFilter((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const confirmRestore = () => {};

  const confirmDelete = () => {
    if (input.reason.length === 0) {
      setError({
        ...error,
        reason: t("error.required", { field: t(`label.reason`) }),
      });
    } else {
      onDeleteLocation(modal.record._id || "", input.reason);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleFilter = async (selection: any) => {
    setFilter((prevFilter) => ({ ...prevFilter, customerType: selection.customerType }));
  };

  const dataTableFilters: FilterSelectionProps[] = [
    {
      key: "Filter",
      label: t("placeholder.filterByCustomerType"),
      name: "customerType",
      value: filter.customerType,
      options: [
        { label: t("label.custTypeAll"), value: "" },
        { label: t("label.custTypeIndividual"), value: CustomerType.INDIVIDUAL },
        { label: t("label.custTypeCompany"), value: CustomerType.COMPANY },
      ],
    },
  ];

  const resetModalState = () => {
    setModal(initialModal);
    setInput(initialInput);
    setError(initialError);
  };

  return (
    <div className={`customer-location ${listType === "Deleted" || isReadonly ? "mt-20" : ""}`}>
      <DataTable
        rowKey={(record: any) => {
          return record?._id;
        }}
        columns={
          listType === "Deleted"
            ? roles[0].type !== "CUSTOMER"
              ? deletedColumns
              : filteredDeletedColumns
            : roles[0].type !== "CUSTOMER"
            ? columns
            : filteredColumns
        }
        dataSource={locationList}
        title={() => (
          <div className="filter-component">
            {roles[0].type !== "CUSTOMER" ? (
              <DataTableFilter
                searchPlaceholder={t("label.searchByCustomerAddress")}
                onSearch={(text: string) => {
                  handleSearch(text);
                }}
                onFilter={(selection) => {
                  handleFilter(selection);
                }}
                renderFilterSelection={dataTableFilters}
              />
            ) : (
              <DataTableFilter
                searchPlaceholder={t("label.searchByCustomerAddress")}
                onSearch={(text: string) => {
                  handleSearch(text);
                }}
              />
            )}
          </div>
        )}
      />
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          setModal(initialModal);
        }}
      >
        {modal.type === "restore" ? (
          <div className="customer-restore-modal">
            <div className="modal-title">{t("label.confirmCustomerRestore")}</div>
            <div>{t("txt.areYouSureRestoreAccount")}</div>
            <div>
              <b>{t("txt.customer").toUpperCase()}: </b> {modal.record?.orgName || ""}
            </div>
            <Row>
              <Col xs={24} sm={12}>
                <b>{t("txt.location").toUpperCase()}: </b> {modal.record?.locationName || ""}
              </Col>
              <Col xs={24} sm={12}>
                <b>{t("txt.locationID").toUpperCase()}: </b> {modal.record?.locationCode || ""}
              </Col>
            </Row>
            <div className="mt-30 right">
              <Button
                type="success"
                label={t("action.confirm")}
                onClick={() => {
                  confirmRestore();
                }}
              />
              <Button
                type="success"
                label={t("action.cancel")}
                className="btn--ghost"
                onClick={() => {
                  setModal(initialModal);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="customer-delete-modal">
            <div className="modal-title">{t("label.deleteCustomerLocation").toUpperCase()}</div>
            <div>{t("txt.areYouSureDeleteLocation")}</div>
            <div>
              <b>{t("txt.customer").toUpperCase()}: </b> {modal.record?.orgName || ""}
            </div>
            <Row>
              <Col xs={24} sm={12}>
                <b>{t("txt.location").toUpperCase()}: </b> {modal.record?.locationName || ""}
              </Col>
              <Col xs={24} sm={12}>
                <b>{t("txt.locationID").toUpperCase()}: </b> {modal.record?.locationCode || ""}
              </Col>
            </Row>
            <div>
              <label className="inp-label inp-label--required">{t("label.enterReason")}</label>
              <Input.TextArea
                id="message"
                size="middle"
                rows={3}
                name="reason"
                value={input.reason}
                onChange={handleChange}
                className="mt-5"
                required={true}
                placeholder={t("label.enterHere")}
              />
              <AlertBox text={error.reason} />
            </div>
            <div className="mt-30 right">
              <Button
                type="danger"
                label={t("action.delete")}
                onClick={() => {
                  confirmDelete();
                }}
              />
              <Button
                type="danger"
                label={t("action.cancel")}
                className="btn--ghost"
                onClick={() => {
                  setModal(initialModal);
                }}
              />
            </div>
          </div>
        )}
      </BasicModal>
    </div>
  );
};

export default CustomerLocationListComponent;
